import {
  CharacterBaseStats,
  CharacterDerivedStats,
  UpgradeComparisonData,
} from "types";

import Tooltip from "components/base/Tooltip";
import TooltipDerivedStat from "components/base/TooltipDerivedStat";
import { DERIVED_STATS } from "data/derivedStats";
import {
  DerivedStatWrapper,
  DerivedStatName,
  StatIconWrapper,
  StatIcon,
  StatValue,
  ValueWrapper,
  StatValueDiff,
  StatDiffIcon,
} from "./DerivedStatsBase";
import { getComparisonIndicator } from "libs/item";

interface DerivedStatProps {
  slug: keyof CharacterDerivedStats;
  value: number;
  baseValue: number;
  upgradeValue: number;
  highlightStat: keyof CharacterBaseStats | null;
  previewDiff?: UpgradeComparisonData | null;
}

export function DerivedStat({
  slug,
  value,
  baseValue,
  upgradeValue,
  highlightStat = null,
  previewDiff = null,
}: DerivedStatProps) {
  const statSlug = slug as keyof CharacterDerivedStats;
  const statInfo = DERIVED_STATS[statSlug];

  const diffValue = previewDiff?.[slug]?.difference || 0;

  const isStatFocusActive = !!highlightStat || !!previewDiff;
  const isHighlightedStat = highlightStat === statInfo.baseStat;
  const isPreviewDiffStat = !!previewDiff?.[slug] && !!diffValue;

  const isDim = isStatFocusActive && !isHighlightedStat && !isPreviewDiffStat;

  return (
    <Tooltip
      color={statInfo.color}
      content={
        <TooltipDerivedStat
          slug={statSlug}
          value={value}
          baseValue={baseValue}
          upgradeValue={upgradeValue}
        />
      }
    >
      <DerivedStatWrapper $isDim={isDim} $color={statInfo.color}>
        <DerivedStatName>
          <StatIconWrapper>
            <StatIcon image={statInfo.image} />
          </StatIconWrapper>
          {statInfo.name}
        </DerivedStatName>
        <ValueWrapper>
          <StatValue $isBeforeValue={isPreviewDiffStat}>
            {statInfo.formatter(value)}
          </StatValue>
          {!!isPreviewDiffStat && (
            <>
              <StatDiffIcon>{getComparisonIndicator([diffValue])}</StatDiffIcon>
              <StatValueDiff>
                {statInfo.formatter(statInfo.rounder(value + diffValue))}
              </StatValueDiff>
            </>
          )}
        </ValueWrapper>
      </DerivedStatWrapper>
    </Tooltip>
  );
}
